import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  createCommand,
  LexicalCommand,
} from 'lexical';

/* ── Command ── */
export const INSERT_IMAGE_COMMAND: LexicalCommand<{ src: string; altText?: string }> =
  createCommand('INSERT_IMAGE_COMMAND');

/* ── Serialized type ── */
type SerializedImageNode = SerializedLexicalNode & {
  src: string;
  altText: string;
  type: 'image';
  version: 1;
};

/* ── Node ── */
export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__key);
  }

  constructor(src: string, altText = '', key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'block';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM() {
    return {
      img: (_node: Node) => ({
        conversion: (domNode: Node) => {
          if (domNode instanceof HTMLImageElement) {
            return { node: new ImageNode(domNode.src, domNode.alt || '') };
          }
          return null;
        },
        priority: 0 as const,
      }),
    };
  }

  exportDOM() {
    const el = document.createElement('img');
    el.setAttribute('src', this.__src);
    el.setAttribute('alt', this.__altText);
    el.setAttribute(
      'style',
      'max-width:100%;display:block;margin:1rem 0;border-radius:8px;',
    );
    return { element: el };
  }

  static importJSON(sn: SerializedImageNode): ImageNode {
    return new ImageNode(sn.src, sn.altText);
  }

  exportJSON(): SerializedImageNode {
    return {
      src: this.__src,
      altText: this.__altText,
      type: 'image',
      version: 1,
    };
  }

  decorate(): JSX.Element {
    return (
      <span style={{ display: 'block' }}>
        <img
          src={this.__src}
          alt={this.__altText}
          style={{
            maxWidth: '100%',
            display: 'block',
            margin: '0.75rem 0',
            borderRadius: '8px',
          }}
        />
      </span>
    );
  }
}

/* ── Helpers ── */
export function $createImageNode(src: string, altText = ''): ImageNode {
  return new ImageNode(src, altText);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
